import React, { useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import Metadata from '../../components/Metadata/Metadata';
import Section from '../../components/Section/Section';
import Card from '../../components/Card/Card';
import GlobalCallout from '../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Wallpaper from '../../components/Wallpaper/Wallpaper';
import Accordion from '../../components/Accordion/Accordion';
import Button from '../../components/Button/Button';
import Image from '../../components/Image/Image';
import ContentSplit from '../../components/ContentSplit/ContentSplit';
import ListItem from '../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="SYN46™ | Virtual Patient Models for Predictive Healthcare"
        description="Discover SYN46™, General Genomics’ innovative solution for disease prediction and preparedness. Explore its virtual patient models and applications in public health and research."
      />
      <Hero
        helper="Innovation"
        headline="Synthetic data (SYN46™) solutions for scalable, secure, and privacy-compliant insights"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Revolutionizing Disease Prediction
        </h2>
        <p className="action-on-view action-fade-in">
          Experience the power of predictive healthcare with SYN46™, a
          cutting-edge solution designed to simulate virtual patient models and
          deliver actionable insights. By leveraging downloadable data sets,
          SYN46™ enables healthcare professionals, researchers, and
          policymakers to forecast disease trajectories, improve preparedness,
          and develop targeted prevention strategies. Discover how SYN46™
          aligns with General Genomics’ mission to advance public health through
          innovative technologies. Contact us today to see how SYN46™ can
          transform your approach to disease prediction and preparedness.
        </p>
        <Image
          path="/images/library/gg46-2.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/lab-technicians-man-laptop.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">Discover More</p>
        <h2 className="action-on-view action-fade-in">Why Choose SYN46™?</h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Unmatched Accuracy:</strong> Harness virtual patient models
            to simulate individual health trajectories with precision.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Downloadable Insights:</strong> Access curated data sets to
            support research and decision-making in real-time.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Broad Applications:</strong> Improve public health, enhance
            clinical research, and bolster disease preparedness efforts.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Innovative Synergy:</strong> Combine SYN46™ with tools like
            Curo46® for comprehensive healthcare solutions.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Proven Impact:</strong> Trusted by researchers and public
            health leaders to drive meaningful change.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More About General Genomics
        </h2>
        <p className="action-on-view action-fade-in">
          Discover additional resources and solutions from General Genomics that
          enhance research, healthcare, and data-driven insights.
        </p>
        <div className="card-group">
          <Card
            icon="science"
            headline="Innovation"
            subhead="Learn about SYN46™ and other cutting-edge solutions that drive genomic advancements."
            btnURL="/innovation"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="business"
            headline="Public Sector Solutions"
            subhead="Explore how our genomic tools support public health initiatives and government research."
            btnURL="/industries/government/public-sector-solutions"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="local_hospital"
            headline="Government Public Health"
            subhead="See how our solutions are transforming healthcare through advanced genomic technologies."
            btnURL="/industries/government/public-health-and-policy"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="data_usage"
            headline="Data Portal"
            subhead="Access a wealth of genomic datasets and resources to support your research and analysis."
            btnURL="/data-portal"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../images/library/lab-technicinan-woman.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently Asked Questions About SYN46™ Disease Prediction Technology
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Explore General Genomics’ SYN46™—downloadable data sets for creating
          virtual patient models that simulate individual health trajectories,
          advancing disease prediction and preparedness in public health and
          research.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What is SYN46™ and how does it work?"
          >
            SYN46™ is an advanced predictive healthcare solution that uses
            virtual patient models to simulate individual health trajectories,
            helping with disease prediction and preparedness. It leverages
            downloadable data sets to create accurate simulations for
            applications in public health, research, and disease prevention.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What are the practical applications of SYN46™?"
          >
            SYN46™ is used across various sectors, including public health,
            clinical research, and government preparedness initiatives. It helps
            healthcare professionals predict disease outcomes and inform
            decision-making for better prevention and response strategies.
          </Accordion>
          <Accordion
            headline="How can I access SYN46™’s data sets and virtual patient models?"
            isOpen={expandAll}
          >
            You can explore and download SYN46™&apos;s data sets directly
            through the <Link to="/data-portal">Data Portal,</Link> which
            provides comprehensive resources for researchers and healthcare
            professionals looking to integrate this technology into their work.
          </Accordion>
          <Accordion
            headline="How does SYN46™ integrate with other General Genomics solutions?"
            isOpen={expandAll}
          >
            SYN46™ works synergistically with other General Genomics solutions,
            like <Link to="/innovation/curo46">Curo46®</Link>, to enhance
            disease prediction and preparedness efforts. This integration helps
            provide more comprehensive healthcare solutions across multiple
            sectors.
          </Accordion>
          <Accordion
            headline="How can I learn more about SYN46™?"
            isOpen={expandAll}
          >
            For more information about SYN46™, contact us today. Our team is
            ready to provide a tailored walkthrough of the product&apos;s
            capabilities and benefits.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
